import React from 'react';
import { Button, Card, Col, Row, Typography } from 'antd';
import {
    ArrowLeftOutlined,
    AuditOutlined,
    DashboardOutlined,
    FileTextOutlined,
    GlobalOutlined,
    MailOutlined,
    SafetyCertificateOutlined,
    TeamOutlined,
    BankOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import { usePageLayout } from '../hooks/usePageLayout';
import { isSuperAdmin } from '../utils/roleUtils';

const sectionCards = [
    {
        key: 'control-centre',
        title: 'Control Centre',
        description: 'Property, access, governance, and onboarding controls in one place.',
        icon: <SafetyCertificateOutlined />,
        path: '/super-admin/control-centre',
    },
    {
        key: 'user-access',
        title: 'User Access',
        description: 'Assign users to one or more properties.',
        icon: <TeamOutlined />,
        path: '/super-admin/user-access',
    },
    {
        key: 'dashboard',
        title: 'Dashboard',
        description: 'Platform performance and portfolio-level trends.',
        icon: <DashboardOutlined />,
        path: '/dashboard',
    },
    {
        key: 'leads',
        title: 'Outreach Contact Queue',
        description: 'Track outreach email prospects, contact status, and contact permission.',
        icon: <TeamOutlined />,
        path: '/leads',
    },
    {
        key: 'outreach',
        title: 'Outreach Monitor',
        description: 'Operations and follow-up monitoring for outreach campaigns.',
        icon: <MailOutlined />,
        path: '/super-admin/outreach',
    },
    {
        key: 'property-manager',
        title: 'Property Manager',
        description: 'Portfolio controls and property-level admin actions.',
        icon: <BankOutlined />,
        path: '/property-manager',
    },
    {
        key: 'website-demos',
        title: 'Website Demos',
        description: 'Demo deployments and website controls.',
        icon: <GlobalOutlined />,
        path: '/website-demos',
    },
    {
        key: 'audit-log',
        title: 'Audit Log',
        description: 'Track changes and operational activity across the platform.',
        icon: <AuditOutlined />,
        path: '/audit-log',
    },
    {
        key: 'customer-onboarding',
        title: 'Customer Onboarding',
        description: 'Super-admin onboarding and rollout guidance.',
        icon: <FileTextOutlined />,
        path: '/customer-onboarding',
    },
];

const SuperAdminOverview = () => {
    const pageLayoutProps = usePageLayout();
    const navigate = useNavigate();
    const { role } = pageLayoutProps;

    if (!isSuperAdmin(role)) {
        return (
            <PageLayout {...pageLayoutProps}>
                <Typography.Title level={4}>Super Admin</Typography.Title>
                <Typography.Text type="secondary">Super admin access is required.</Typography.Text>
            </PageLayout>
        );
    }

    return (
        <PageLayout {...pageLayoutProps}>
            <div style={{ maxWidth: 1200, margin: '0 auto' }}>
                <Button
                    type="text"
                    icon={<ArrowLeftOutlined />}
                    onClick={() => navigate('/')}
                    style={{ paddingLeft: 0, marginBottom: 12, fontWeight: 600 }}
                >
                    Back
                </Button>
                <Typography.Title level={2} style={{ marginBottom: 8 }}>Super Admin</Typography.Title>
                <Typography.Paragraph type="secondary" style={{ marginBottom: 24 }}>
                    Operational controls and platform-level tools are grouped here so customer-facing admin tasks stay separate.
                </Typography.Paragraph>

                <Row gutter={[16, 16]}>
                    {sectionCards.map((card) => (
                        <Col xs={24} md={12} lg={8} key={card.key}>
                            <Card
                                hoverable
                                style={{ height: '100%' }}
                                onClick={() => navigate(card.path)}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                                    <div style={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: 12,
                                        background: '#e8f1ff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#1d4ed8',
                                        fontSize: 18,
                                    }}>
                                        {card.icon}
                                    </div>
                                    <Typography.Title level={4} style={{ margin: 0 }}>{card.title}</Typography.Title>
                                </div>
                                <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
                                    {card.description}
                                </Typography.Paragraph>
                                <Button type="primary" ghost onClick={() => navigate(card.path)}>
                                    Open
                                </Button>
                            </Card>
                        </Col>
                    ))}
                </Row>
            </div>
        </PageLayout>
    );
};

export default SuperAdminOverview;
